import React from 'react';
import { useTranslation } from 'react-i18next';
import { s } from '../helpers';

export interface AccessAccountItem {
  login: string;
  platform: string;
  capital: string;
  accountId: string;
  alias: string;
  status: string;
  stColor: string;
  stBg: string;
  onToggle: () => void;
  border: string;
  bg: string;
  boxBorder: string;
  boxBg: string;
  loginColor: string;
}

export interface AccessUserRowItem {
  userId: string;
  name: string;
  email: string;
  accCount: string;
  logins: string;
  loginColor: string;
  requested: string;
  status: string;
  stColor: string;
  stBg: string;
  bg: string;
  boxBorder: string;
  boxBg: string;
  boxIcon: string;
  expanded: boolean;
  chev: string;
  onExpand: (e: React.MouseEvent) => void;
  onToggle: () => void;
  accs: AccessAccountItem[];
}

export interface AccessTabItem {
  label: string;
  bg: string;
  color: string;
  onClick: () => void;
}

export interface AccessManagementModalProps {
  isOpen: boolean;
  closeModal: () => void;
  accessTabs: AccessTabItem[];
  accessSearch: string;
  onAccessSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  toggleAllAccess: () => void;
  allBorder: string;
  allBg: string;
  allIcon: string;
  accessRows: AccessUserRowItem[];
  accessEmpty: boolean;
  accessSelCount: number;
  accessSelUsers: number;
  revokeSelected: () => void;
  revokeBorder: string;
  revokeColor: string;
  revokeCount: number;
  approveSelected: () => void;
  approveBg: string;
  approveCount: number;
}

export const AccessManagementModal: React.FC<AccessManagementModalProps> = ({
  isOpen,
  closeModal,
  accessTabs,
  accessSearch,
  onAccessSearch,
  toggleAllAccess,
  allBorder,
  allBg,
  allIcon,
  accessRows,
  accessEmpty,
  accessSelCount,
  accessSelUsers,
  revokeSelected,
  revokeBorder,
  revokeColor,
  revokeCount,
  approveSelected,
  approveBg,
  approveCount
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      onClick={closeModal}
      style={s(
        `position:fixed;inset:0;background:rgba(1,37,60,0.45);z-index:9999;display:flex;align-items:flex-start;justify-content:center;padding:6vh 12px;overflow-y:auto`
      )}
    >
      <div
        onClick={stop}
        style={s(
          `width:100%;max-width:980px;background:#fff;border-radius:14px;box-shadow:0 24px 64px rgba(1,37,60,0.3);overflow:hidden`
        )}
      >
        <div
          style={s(
            `display:flex;align-items:flex-start;justify-content:space-between;gap:12px;padding:20px 24px;border-bottom:1px solid #EEF0F3`
          )}
        >
          <div style={s(`display:flex;flex-direction:column;gap:4px`)}>
            <div style={s(`display:flex;align-items:center;gap:8px`)}>
              <span style={s(`font-size:18px;font-weight:700;color:#0F1B2D`)}>
                {t('managerNetwork.accessModal.title', 'Capital Manager Access')}
              </span>
              <span style={s(`font-size:10.5px;font-weight:700;color:#8C6424;background:#FBF1DE;border-radius:4px;padding:2px 6px`)}>
                {t('managerNetwork.accessModal.admin', 'ADMIN')}
              </span>
            </div>
            <span style={s(`font-size:13px;color:#6B7280;text-wrap:pretty;max-width:680px`)}>
              {t('managerNetwork.accessModal.desc', 'Only users added by an admin appear in the Capital Manager Network. Select a user to add all of their trading accounts, or expand the row to pick individual accounts. New accounts start as Unclassified until a stage is set.')}
            </span>
          </div>
          <button
            onClick={closeModal}
            style={s(
              `width:34px;height:34px;border-radius:8px;border:1px solid #E3E6EA;background:#fff;cursor:pointer;font-size:16px;display:flex;align-items:center;justify-content:center;flex:none`
            )}
          >
            <i className="icon-x"></i>
          </button>
        </div>
        <div
          style={s(
            `display:flex;flex-wrap:wrap;gap:10px;align-items:center;padding:12px 24px;border-bottom:1px solid #EEF0F3`
          )}
        >
          <div style={s(`display:flex;border:1px solid #E6DCCB;border-radius:8px;overflow:hidden`)}>
            {accessTabs?.map((at, at_idx) => (
              <React.Fragment key={at_idx}>
                <button
                  onClick={at.onClick}
                  style={s(
                    `height:36px;padding:0 14px;border:0;background:${at.bg};color:${at.color};font-size:13px;font-weight:500;cursor:pointer`
                  )}
                >
                  {at.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          <div
            style={s(
              `flex:1 1 260px;max-width:380px;height:38px;display:flex;align-items:center;gap:8px;padding:0 12px;border:1px solid #E3E6EA;border-radius:8px`
            )}
          >
            <i className="icon-search" style={s(`color:#6B7280`)}></i>
            <input
              value={accessSearch}
              onChange={onAccessSearch}
              placeholder={t('managerNetwork.accessModal.searchPlaceholder', 'Search by user ID, email or trading account no...')}
              style={s(`border:0;outline:0;flex:1;font-size:14px;min-width:0`)}
            />
          </div>
        </div>
        <div style={s(`overflow-x:auto;max-height:56vh;overflow-y:auto`)}>
          <div style={s(`min-width:880px`)}>
            <div
              style={s(
                `display:grid;grid-template-columns:40px 90px minmax(130px,1fr) minmax(190px,1.3fr) minmax(200px,1.5fr) 96px 110px;gap:8px;align-items:center;padding:10px 24px;background:#FAFAFB;font-size:11.5px;font-weight:600;color:#6B7280;text-transform:uppercase;letter-spacing:0.03em;position:sticky;top:0;z-index:1`
              )}
            >
              <span
                onClick={toggleAllAccess}
                style={s(
                  `width:18px;height:18px;border-radius:4px;border:1.5px solid ${allBorder};background:${allBg};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;cursor:pointer`
                )}
              >
                <i className={`icon-${allIcon}`}></i>
              </span>
              <span>{t('managerNetwork.accessModal.cols.userId', 'User ID')}</span>
              <span>{t('managerNetwork.accessModal.cols.name', 'Name')}</span>
              <span>{t('managerNetwork.accessModal.cols.email', 'Email')}</span>
              <span>{t('managerNetwork.accessModal.cols.tradingAccounts', 'Trading accounts')}</span>
              <span>{t('managerNetwork.accessModal.cols.created', 'Created')}</span>
              <span>{t('managerNetwork.accessModal.cols.status', 'Status')}</span>
            </div>
            {accessRows?.map((u, u_idx) => (
              <React.Fragment key={u_idx}>
                <div style={s(`border-bottom:1px solid #F1F2F5;background:${u.bg}`)}>
                  <div
                    onClick={u.onToggle}
                    style={s(
                      `display:grid;grid-template-columns:40px 90px minmax(130px,1fr) minmax(190px,1.3fr) minmax(200px,1.5fr) 96px 110px;gap:8px;align-items:center;padding:10px 24px;font-size:13px;cursor:pointer`
                    )}
                  >
                    <span
                      style={s(
                        `width:18px;height:18px;border-radius:4px;border:1.5px solid ${u.boxBorder};background:${u.boxBg};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px`
                      )}
                    >
                      <i className={`icon-${u.boxIcon}`}></i>
                    </span>
                    <span style={s(`font-weight:600;color:#0F1B2D`)}>{u.userId}</span>
                    <span style={s(`color:#1B2430;white-space:nowrap;overflow:hidden;text-overflow:ellipsis`)}>{u.name}</span>
                    <span style={s(`color:#1B2430;white-space:nowrap;overflow:hidden;text-overflow:ellipsis`)}>{u.email}</span>
                    <button
                      onClick={u.onExpand}
                      style={s(
                        `border:0;background:transparent;padding:0;text-align:left;display:flex;align-items:center;gap:6px;cursor:pointer;min-width:0`
                      )}
                    >
                      <i className={`icon-chevron-${u.chev}`} style={s(`color:#8C6424;font-size:14px;flex:none`)}></i>
                      <span style={s(`display:flex;flex-direction:column;gap:2px;min-width:0`)}>
                        <span style={s(`color:#1B2430;font-size:12.5px;font-weight:500`)}>{u.accCount}</span>
                        <span
                          style={s(
                            `color:${u.loginColor};font-size:11.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis`
                          )}
                        >
                          {u.logins}
                        </span>
                      </span>
                    </button>
                    <span style={s(`color:#6B7280;font-size:12px`)}>{u.requested}</span>
                    <span>
                      <span
                        style={s(
                          `font-size:11.5px;font-weight:600;color:${u.stColor};background:${u.stBg};padding:3px 8px;border-radius:10px;white-space:nowrap`
                        )}
                      >
                        {u.status}
                      </span>
                    </span>
                  </div>
                  {Boolean(u.expanded) && (
                    <div style={s(`padding:0 24px 10px 64px;display:flex;flex-direction:column;gap:4px`)}>
                      {u.accs?.map((ac, ac_idx) => (
                        <React.Fragment key={ac_idx}>
                          <div
                            onClick={ac.onToggle}
                            style={s(
                              `display:grid;grid-template-columns:28px 130px 90px 110px minmax(0,1fr) 110px;gap:8px;align-items:center;padding:8px 10px;border:1px solid ${ac.border};border-radius:8px;background:${ac.bg};font-size:12.5px;cursor:pointer`
                            )}
                          >
                            <span
                              style={s(
                                `width:16px;height:16px;border-radius:4px;border:1.5px solid ${ac.boxBorder};background:${ac.boxBg};color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px`
                              )}
                            >
                              <i className="icon-check"></i>
                            </span>
                            <span style={s(`font-weight:600;color:${ac.loginColor}`)}>#{ac.login}</span>
                            <span style={s(`color:#1B2430`)}>{ac.platform}</span>
                            <span style={s(`color:#1B2430`)}>{ac.capital}</span>
                            <span style={s(`color:#6B7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis`)}>
                              {ac.accountId} · {ac.alias}
                            </span>
                            <span>
                              <span
                                style={s(
                                  `font-size:11px;font-weight:600;color:${ac.stColor};background:${ac.stBg};padding:2px 8px;border-radius:10px;white-space:nowrap`
                                )}
                              >
                                {ac.status}
                              </span>
                            </span>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
            {Boolean(accessEmpty) && (
              <div style={s(`padding:32px;text-align:center;color:#6B7280;font-size:14px`)}>
                {t('managerNetwork.accessModal.noUsers', 'No users match this search.')}
              </div>
            )}
          </div>
        </div>
        <div
          style={s(
            `display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:12px;padding:16px 24px;border-top:1px solid #EEF0F3`
          )}
        >
          <span style={s(`font-size:13px;color:#6B7280`)}>
            {t('managerNetwork.accessModal.selectedSummary', '{{accCount}} accounts selected · {{userCount}} users', {
              accCount: accessSelCount,
              userCount: accessSelUsers
            })}
          </span>
          <div style={s(`display:flex;flex-wrap:wrap;gap:10px`)}>
            <button
              onClick={closeModal}
              style={s(
                `height:40px;padding:0 18px;border:1px solid #E3E6EA;border-radius:8px;background:#fff;color:#1B2430;font-size:14px;font-weight:500;cursor:pointer`
              )}
            >
              {t('managerNetwork.accessModal.close', 'Close')}
            </button>
            <button
              onClick={revokeSelected}
              style={s(
                `height:40px;padding:0 18px;border:1px solid ${revokeBorder};border-radius:8px;background:#fff;color:${revokeColor};font-size:14px;font-weight:500;cursor:pointer;display:flex;align-items:center;gap:6px`
              )}
            >
              <i className="icon-user-minus"></i>
              {t('managerNetwork.accessModal.remove', 'Remove ({{count}})', { count: revokeCount })}
            </button>
            <button
              onClick={approveSelected}
              style={s(
                `height:40px;padding:0 18px;border:0;border-radius:8px;background:${approveBg};color:#fff;font-size:14px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px`
              )}
            >
              <i className="icon-user-plus"></i>
              {t('managerNetwork.accessModal.addToNetwork', 'Add to network ({{count}})', { count: approveCount })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
